/**
 * شاشة «مكتب الحسابات» — الدفاتر المالية وربط الدفعة بأسرتها.
 *
 * 🔴 **ليه الشاشة دي أهم من شكلها:** الدفاتر دخلت السيستم كاملة وكل رقم
 *    اتطابق مع مجاميع الشيت — **بس ٣٨٧ دفعة من ٤٠٦ مالهاش أسرة**
 *    ($43,741). يعني السيستم شايف الفلوس ومش عارف مين دفعها، وسؤال زي
 *    «الأسرة دي دفعت كام؟» مالوش إجابة.
 *
 * 🔑 **والسبب مش عطل:** عمود اسم الدافع في الشيت **كنية حرّة مش مفتاح** —
 *    ٢٣٦ هجاء فريد مقابل ٣٢٦ أسرة، والشخص الواحد بيتكتب بأكتر من صيغة.
 *    اتقاس: التطابق التام بيدّي ١٩، وتلات توسعات آمنة رجّعوا **نفس الـ١٩**.
 *
 * ⛔ **وعشان كده مفيش ترتيب «الأقرب» ولا اقتراح تلقائي في المنتقي.**
 *    الترتيب بالتشابه بيدفع الموظف يدوس على أول صف — وده تخمين بواجهة
 *    لطيفة. وحطّ فلوس أسرة تحت أسرة تانية غلطة **ما بتظهرش في أي تقرير**؛
 *    بتظهر لما ولي أمر يقول «أنا دفعت».
 *
 * 🔑 **اللي بيخلّي المهمة قابلة للإنجاز:** لما الموظف يربط هجاء، السيرفر
 *    بيربط كل الدفعات غير المربوطة اللي اسمها الخام **مطابق حرفيًا**.
 *    فالمهمة ٢٣٦ قرار مش ٤٠٦.
 *
 * ⚠️ **الأرقام نصوص** جاية من السيرفر وبتتعرض زي ما هي — `Number()` هنا
 *    بيرجّعنا لـ`float` وكسور ضايعة في تقرير فلوس.
 *
 * 🔒 العزل من السيرفر: القسم اللي مش من قرّاء الفلوس بياخد 403 قبل ما
 *    يوصل للاستعلام. الشاشة **ما بتخفيش حاجة بنفسها** — بتعرض اللي وصلها.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Shell } from "@/components/Shell";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  NoPermissionState,
  NoResultsState,
  OfflineState,
} from "@/components/states/ScreenStates";
import { Dash, DateAr, NeedsReviewMark } from "@/components/RegistryBits";
import { fmtNum } from "@/lib/format";
import { ApiError, errorMessageAr, OfflineError } from "@/api/client";
import { searchFamilies } from "@/api/registry";
import { buildFinancePayload, readXlsx, XlsxError, type XlsxSheet } from "@/lib/xlsx";
import {
  getFinanceSummary,
  linkPayment,
  listPayments,
  listStaffPayouts,
  listTeacherPayouts,
  unlinkPayment,
  type FinanceSummary,
  type PaymentRow,
  type PaymentsMeta,
  type PayoutsMeta,
  type StaffPayoutRow,
  type TeacherPayoutRow,
  uploadFinanceBooks,
  type ImportPreview,
} from "@/api/finance";
import "@/styles/registry.css";

const PER_PAGE = 15;
const MIN_SEARCH = 2;
const SEARCH_DEBOUNCE_MS = 350;

/**
 * ⚠️ المبلغ **نص** جاي من السيرفر. بنفصل الكسر ونعرضه زي ما هو بدل ما
 *    نعدّيه على `Number` — ده بيحافظ على الخانات كلها.
 */
function Money({ v, cur = "$" }: { v: string | null; cur?: string }) {
  if (v === null || v === "") return <Dash />;
  const neg = v.startsWith("-");
  const body = neg ? v.slice(1) : v;
  const [whole, frac] = body.split(".");
  const grouped = fmtNum(Number(whole)) === "NaN" ? whole : (whole ?? "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  // الكسر بيتقص لخانتين للعرض بس — القيمة الأصلية ما بتتلمسش
  const tail = frac ? `.${frac.slice(0, 2)}` : "";
  return (
    <bdi className="num" style={{ whiteSpace: "nowrap" }}>
      {neg ? "−" : ""}{cur}{grouped}{tail}
    </bdi>
  );
}

/** «2026-01» ⇒ «يناير ٢٠٢٦» بأرقام لاتينية (قاعدة السيستم) */
const MONTH_AR = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
                  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
function monthLabel(m: string): string {
  const parts = m.split("-");
  const y = parts[0];
  const i = Number(parts[1]) - 1;
  return MONTH_AR[i] ? `${MONTH_AR[i]} ${y}` : m;
}

type Tab = "payments" | "teachers" | "staff" | "upload";
const TABS: { key: Tab; label: string }[] = [
  { key: "payments", label: "المتحصلات" },
  { key: "teachers", label: "رواتب المعلمين" },
  { key: "staff", label: "رواتب الموظفين" },
  { key: "upload", label: "رفع دفاتر الشهر" },
];

export function Accounts() {
  const [tab, setTab] = useState<Tab>("payments");
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [denied, setDenied] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [month, setMonth] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const debounce = useRef<number | null>(null);
  useEffect(() => {
    const trimmed = searchInput.trim();
    const next = trimmed.length >= MIN_SEARCH ? trimmed : "";
    if (debounce.current) window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => setQuery((c) => (c === next ? c : next)),
                                          SEARCH_DEBOUNCE_MS);
    return () => { if (debounce.current) window.clearTimeout(debounce.current); };
  }, [searchInput]);

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await getFinanceSummary());
      setDenied(false);
    } catch (err) {
      // 🔒 403 = قسمك مش من قرّاء الفلوس. **مش عطل** — ورسالة صريحة أحسن
      //    من شاشة فاضية بتخلّي الموظف يفتكر إن السيستم واقع.
      if (err instanceof ApiError && err.status === 403) { setDenied(true); return; }
      console.error("[accounts] الملخّص المالي فشل", err);
    }
  }, []);
  useEffect(() => { void loadSummary(); }, [loadSummary, reloadKey]);

  if (denied) {
    return (
      <Shell title="مكتب الحسابات">
        <NoPermissionState messageAr="الدفاتر المالية مش من صلاحيات قسمك" />
      </Shell>
    );
  }

  const months = summary?.months ?? [];

  return (
    <Shell
      title="مكتب الحسابات"
      {...(tab === "upload"
        ? {}
        : {
            search: {
              value: searchInput,
              onChange: setSearchInput,
              placeholder: tab === "payments" ? "ابحث باسم الدافع…" : "ابحث بالاسم…",
            },
          })}
    >
      {summary ? <SummaryCards s={summary} onGoUnlinked={() => setTab("payments")} /> : null}

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {TABS.map((t) => (
          <button key={t.key} type="button"
                  className={`filt${tab === t.key ? " on" : ""}`}
                  onClick={() => { setTab(t.key); setSearchInput(""); }}>
            {t.label}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        {/* ⚠️ فلتر الشهر مالوش معنى وإنت بترفع — إخفاؤه أوضح من تعطيله */}
        {tab !== "upload" ? (
          <select className="filt" value={month} onChange={(e) => setMonth(e.target.value)}
                  aria-label="الشهر">
            <option value="">كل الشهور</option>
            {months.map((m) => (
              <option key={m.month} value={m.month}>{monthLabel(m.month)}</option>
            ))}
          </select>
        ) : null}
      </div>

      {tab === "payments" ? (
        <PaymentsTab query={query} month={month} onClearSearch={() => setSearchInput("")}
                     onChanged={() => setReloadKey((k) => k + 1)} reloadKey={reloadKey} />
      ) : null}
      {tab === "teachers" ? <TeachersTab query={query} month={month}
                                         onClearSearch={() => setSearchInput("")} /> : null}
      {tab === "staff" ? <StaffTab query={query} month={month}
                                   onClearSearch={() => setSearchInput("")} /> : null}
      {/* ⚠️ **ما بننقلوش لتبويب تاني بعد الكتابة**: الموظف دوس «اكتب» ولازم
          يشوف اللي اتكتب. النقل الفوري بيخلّيه مش متأكد إن حاجة حصلت أصلًا. */}
      {tab === "upload" ? (
        <UploadTab onImported={() => setReloadKey((k) => k + 1)}
                   onGoPayments={() => setTab("payments")} />
      ) : null}
    </Shell>
  );
}

/* ══════════════════════════ بطاقات الملخّص ══════════════════════════ */

function SummaryCards({ s, onGoUnlinked }: { s: FinanceSummary; onGoUnlinked: () => void }) {
  const gifts = s.months.reduce((acc, m) => acc + Number(m.gifts || 0), 0);
  return (
    <div className="reg-card" style={{ padding: 14 }}>
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-start" }}>
        <Stat label="دفعات مسجّلة" value={<span className="num">{fmtNum(s.paymentsCount)}</span>} />
        <Stat label="رواتب معلمين" value={<Money v={s.teacherPayoutsNetUsd} />}
              sub={`${fmtNum(s.teacherPayoutsCount)} راتب`} />
        <Stat label="رواتب موظفين" value={<Money v={s.staffPayoutsEgp} cur="ج.م " />}
              sub={`${fmtNum(s.staffPayoutsCount)} راتب`} />
        <span style={{ flex: 1 }} />
        {/* 🔑 الرقم ده هو سبب وجود الشاشة — مش إحصائية جانبية */}
        <button type="button" className="filt on" onClick={onGoUnlinked}
                style={{ textAlign: "start", lineHeight: 1.7 }}>
          <div className="t-meta">فلوس لسه مش منسوبة لأسرة</div>
          <div style={{ fontSize: "1.15rem", fontWeight: 700 }}>
            <Money v={s.unlinkedTotal} />
          </div>
          <div className="t-meta">
            <span className="num">{fmtNum(s.unlinkedCount)}</span> دفعة — اضغط لتربطها
          </div>
        </button>
      </div>
      {gifts > 0 ? (
        <div className="t-meta" style={{ marginTop: 10 }}>
          ⚠️ الهدايا (<Money v={String(gifts)} />) <strong>مش داخلة</strong> في «الاجمالى» بتاع دفتر
          الأكاديمية — معروضة هنا لوحدها عشان الرقمين ما يختلفوش من غير ما حد يعرف ليه.
        </div>
      ) : null}
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }) {
  return (
    <div style={{ lineHeight: 1.7 }}>
      <div className="t-meta">{label}</div>
      <div style={{ fontSize: "1.15rem", fontWeight: 700 }}>{value}</div>
      {sub ? <div className="t-meta">{sub}</div> : null}
    </div>
  );
}

/* ══════════════════════════ تبويب المتحصلات ══════════════════════════ */

type PayState =
  | { kind: "loading" }
  | { kind: "ready"; rows: PaymentRow[]; meta: PaymentsMeta }
  | { kind: "error"; messageAr: string }
  | { kind: "offline" };

/** الشرايح — «محتاجة ربط» أول واحدة عن قصد: هي سبب وجود الشاشة. */
const SEGMENTS = [
  { key: "unlinked", label: "محتاجة ربط" },
  { key: "linked", label: "مربوطة" },
  { key: "all", label: "الكل" },
] as const;
type Segment = (typeof SEGMENTS)[number]["key"];

function PaymentsTab({ query, month, onClearSearch, onChanged, reloadKey }: {
  query: string; month: string; onClearSearch: () => void;
  onChanged: () => void; reloadKey: number;
}) {
  const [segment, setSegment] = useState<Segment>("unlinked");
  const [page, setPage] = useState(1);
  const [state, setState] = useState<PayState>({ kind: "loading" });
  const [picking, setPicking] = useState<PaymentRow | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => { setPage(1); }, [query, month, segment]);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const { rows, meta } = await listPayments({
        page, perPage: PER_PAGE, q: query || undefined, month: month || undefined,
        linked: segment === "all" ? undefined : segment === "linked",
      });
      setState({ kind: "ready", rows, meta });
    } catch (err) {
      console.error("[accounts] تحميل المتحصلات فشل", err);
      setState(err instanceof OfflineError
        ? { kind: "offline" }
        : { kind: "error", messageAr: errorMessageAr(err) });
    }
  }, [page, query, month, segment]);
  useEffect(() => { void load(); }, [load, reloadKey]);

  const rows = state.kind === "ready" ? state.rows : [];
  const meta = state.kind === "ready" ? state.meta : null;

  async function doUnlink(row: PaymentRow) {
    try {
      setToast(await unlinkPayment(row.id));
      await load();
      onChanged();
    } catch (err) {
      setToast(errorMessageAr(err));
    }
  }

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                    marginTop: 12 }}>
        {SEGMENTS.map((s) => (
          <button key={s.key} type="button"
                  className={`filt${segment === s.key ? " on" : ""}`}
                  onClick={() => setSegment(s.key)}>
            {s.label}
            {meta && s.key === "unlinked" ? (
              <> · <span className="num">{fmtNum(meta.unlinkedCount)}</span></>
            ) : null}
          </button>
        ))}
        {meta && segment === "unlinked" && meta.unlinkedCount > 0 ? (
          <span className="t-meta">
            اربط هجاء واحد ⇒ كل الدفعات بنفس الاسم بالحرف بتترَبط معاه
          </span>
        ) : null}
      </div>

      {toast ? (
        <div className="reg-card" style={{ padding: 10, marginTop: 10 }}
             role="status" aria-live="polite">
          {toast}{" "}
          <button type="button" className="filt" onClick={() => setToast(null)}>تمام</button>
        </div>
      ) : null}

      <div className="reg-card" style={{ marginTop: 12 }}>
        <div className="reg-head">
          <span className="sec-title">
            {meta ? (<><span className="num">{fmtNum(meta.total)}</span> دفعة</>) : "المتحصلات"}
            {query ? ` · بحث عن «${query}»` : ""}
            {month ? ` · ${monthLabel(month)}` : ""}
          </span>
        </div>

        {state.kind === "loading" ? <LoadingState rows={5} /> : null}
        {state.kind === "offline" ? <OfflineState onRetry={() => void load()} /> : null}
        {state.kind === "error" ? (
          <ErrorState messageAr={state.messageAr} onRetry={() => void load()} />
        ) : null}

        {state.kind === "ready" && rows.length === 0 && query ? (
          <NoResultsState query={query} onClear={onClearSearch} />
        ) : null}
        {state.kind === "ready" && rows.length === 0 && !query && segment === "unlinked" ? (
          <EmptyState title="كل الدفعات منسوبة لأسرها 🌿"
                      hint="مفيش فلوس معلّقة — ده خبر كويس"
                      action={{ label: "شوف الكل", onClick: () => setSegment("all") }} />
        ) : null}
        {state.kind === "ready" && rows.length === 0 && !query && segment !== "unlinked" ? (
          <EmptyState title="لسه مفيش متحصلات"
                      hint="الدفاتر بتترفع من مسار الاستيراد" />
        ) : null}

        {rows.length > 0 ? (
          <div className="reg-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>الدافع (زي ما هو في الشيت)</th>
                  <th>الأسرة</th>
                  <th>المبلغ</th>
                  <th>القناة</th>
                  <th>التاريخ</th>
                  <th>الشهر</th>
                  <th>الحالة</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      {/* ⚠️ الاسم لاتيني في مصدر عربي — `<bdi>` بلا `dir` */}
                      <bdi>{r.payer_name_raw || <Dash />}</bdi>
                      {r.data_quality.needs_review ? (
                        <NeedsReviewMark q={r.data_quality} compact />
                      ) : null}
                    </td>
                    <td>
                      {r.family_name_ar ? <bdi>{r.family_name_ar}</bdi>
                                        : <span className="t-meta">— مش منسوبة</span>}
                    </td>
                    <td><Money v={r.amount} /></td>
                    <td className="t-desc">{r.channel_ar}</td>
                    <td><DateAr v={r.paid_at} /></td>
                    <td className="t-desc">{monthLabel(r.month)}</td>
                    <td className="t-desc">{r.status_raw || r.status_ar}</td>
                    <td style={{ textAlign: "end", whiteSpace: "nowrap" }}>
                      {r.family_id ? (
                        <button type="button" className="filt"
                                onClick={() => void doUnlink(r)}>فكّ الربط</button>
                      ) : (
                        <button type="button" className="filt on"
                                onClick={() => setPicking(r)}>اربط بأسرة</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {meta && meta.totalPages > 1 ? (
          <Pager page={meta.page} totalPages={meta.totalPages} total={meta.total}
                 onPage={setPage} />
        ) : null}
      </div>

      {picking ? (
        <FamilyPicker
          payment={picking}
          onClose={() => setPicking(null)}
          onDone={async (msg) => {
            setPicking(null);
            setToast(msg);
            await load();
            onChanged();
          }}
        />
      ) : null}
    </>
  );
}

/* ══════════════════════════ منتقي الأسرة ══════════════════════════ */

/**
 * ⛔ **بيبدأ فاضي عن قصد.** أي اقتراح تلقائي — حتى «الأقرب للاسم» —
 *    بيدفع الموظف يقبل تخمين الآلة. الموظف بيدوّر بنفسه ويقرر بنفسه.
 */
function FamilyPicker({ payment, onClose, onDone }: {
  payment: PaymentRow;
  onClose: () => void;
  onDone: (toast: string) => void | Promise<void>;
}) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<{ id: string; name_ar: string;
                                           family_code: string; country: string | null }[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [spread, setSpread] = useState(true);
  const debounce = useRef<number | null>(null);

  useEffect(() => {
    const t = term.trim();
    if (debounce.current) window.clearTimeout(debounce.current);
    if (t.length < MIN_SEARCH) { setResults([]); return; }
    debounce.current = window.setTimeout(async () => {
      try {
        setResults(await searchFamilies(t));
        setErr(null);
      } catch (e) {
        console.error("[accounts] بحث الأسر فشل", e);
        setErr(errorMessageAr(e));
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => { if (debounce.current) window.clearTimeout(debounce.current); };
  }, [term]);

  async function choose(familyId: string) {
    setBusy(true);
    try {
      const res = await linkPayment(payment.id, familyId, spread);
      await onDone(res.toastAr);
    } catch (e) {
      setErr(errorMessageAr(e));
      setBusy(false);
    }
  }

  return (
    <div role="dialog" aria-modal="true" aria-label="ربط الدفعة بأسرة"
         className="modal-scrim"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="reg-card" style={{ width: "min(560px, 100%)", padding: 16 }}>
        <div className="sec-title" style={{ marginBottom: 4 }}>اربط الدفعة بأسرة</div>
        <div className="t-meta" style={{ lineHeight: 1.9 }}>
          الشيت كاتب الدافع: <bdi style={{ fontWeight: 700 }}>{payment.payer_name_raw || "—"}</bdi>
          {" · "}<Money v={payment.amount} />{" · "}{monthLabel(payment.month)}
          {payment.notes_ar ? <> · <bdi>{payment.notes_ar}</bdi></> : null}
        </div>

        <input
          className="inp" autoFocus value={term} onChange={(e) => setTerm(e.target.value)}
          placeholder="اكتب اسم الأسرة أو كودها…"
          aria-label="ابحث عن الأسرة"
          style={{ width: "100%", marginTop: 12 }}
        />

        {err ? <div className="t-meta" style={{ color: "var(--danger, #b00)" }}>{err}</div> : null}

        <div style={{ maxHeight: 260, overflowY: "auto", marginTop: 10 }}>
          {term.trim().length < MIN_SEARCH ? (
            <div className="t-meta" style={{ padding: 10 }}>
              اكتب حرفين على الأقل. ⛔ مفيش اقتراح تلقائي — الربط قرارك إنت،
              وفلوس أسرة تحت أسرة تانية غلطة ما بتظهرش في أي تقرير.
            </div>
          ) : results.length === 0 ? (
            <div className="t-meta" style={{ padding: 10 }}>مفيش أسرة بالاسم ده</div>
          ) : (
            results.map((f) => (
              <button key={f.id} type="button" className="filt" disabled={busy}
                      onClick={() => void choose(f.id)}
                      style={{ display: "block", width: "100%", textAlign: "start",
                               marginBottom: 6 }}>
                <bdi style={{ fontWeight: 600 }}>{f.name_ar}</bdi>
                <span className="t-meta"> · <bdi>{f.family_code}</bdi>
                  {f.country ? <> · {f.country}</> : null}</span>
              </button>
            ))
          )}
        </div>

        <label style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 10 }}>
          <input type="checkbox" checked={spread} onChange={(e) => setSpread(e.target.checked)} />
          <span className="t-meta" style={{ lineHeight: 1.8 }}>
            اربط كمان كل دفعة <strong>غير مربوطة</strong> اسمها في الشيت مطابق بالحرف
            (<bdi>{payment.payer_name_raw || "—"}</bdi>).
            ⚠️ الدفعات المربوطة بأسرة تانية ما بتتلمسش.
          </span>
        </label>

        <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
          <button type="button" className="filt" onClick={onClose} disabled={busy}>إلغاء</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════ تبويبات الرواتب ══════════════════════════ */

type PayoutState<T> =
  | { kind: "loading" }
  | { kind: "ready"; rows: T[]; meta: PayoutsMeta }
  | { kind: "error"; messageAr: string }
  | { kind: "offline" };

function usePayouts<T>(
  fetcher: (o: { q?: string; month?: string; page: number; perPage: number })
    => Promise<{ rows: T[]; meta: PayoutsMeta }>,
  query: string, month: string,
) {
  const [page, setPage] = useState(1);
  const [state, setState] = useState<PayoutState<T>>({ kind: "loading" });
  useEffect(() => { setPage(1); }, [query, month]);
  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const { rows, meta } = await fetcher({ q: query || undefined,
                                             month: month || undefined,
                                             page, perPage: PER_PAGE });
      setState({ kind: "ready", rows, meta });
    } catch (err) {
      console.error("[accounts] تحميل الرواتب فشل", err);
      setState(err instanceof OfflineError
        ? { kind: "offline" }
        : { kind: "error", messageAr: errorMessageAr(err) });
    }
  }, [fetcher, query, month, page]);
  useEffect(() => { void load(); }, [load]);
  return { state, load, setPage };
}

function TeachersTab({ query, month, onClearSearch }:
                     { query: string; month: string; onClearSearch: () => void }) {
  const { state, load, setPage } = usePayouts<TeacherPayoutRow>(listTeacherPayouts, query, month);
  const rows = state.kind === "ready" ? state.rows : [];
  const meta = state.kind === "ready" ? state.meta : null;
  return (
    <div className="reg-card" style={{ marginTop: 12 }}>
      <div className="reg-head">
        <span className="sec-title">
          {meta ? (<><span className="num">{fmtNum(meta.total)}</span> راتب · إجمالي{" "}
                     <Money v={meta.sum} /></>) : "رواتب المعلمين"}
        </span>
      </div>
      <PayoutStates state={state} query={query} onRetry={() => void load()}
                    onClear={onClearSearch} emptyTitle="لسه مفيش رواتب معلمين" />
      {rows.length > 0 ? (
        <div className="reg-scroll">
          <table className="tbl">
            <thead>
              <tr>
                <th>المعلم</th><th>الشهر</th><th>الساعات</th><th>الصافي</th>
                <th>سعر الصرف</th><th>بالمصري</th><th>المرسل</th><th>الصرف</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                // 🔑 مجموع الساعات من **كل** السطور: المعلم ممكن يكون له
                //    أكتر من سعر في نفس الشهر، وعرض سطر واحد بيكدب.
                const hours = r.lines.reduce((a, l) => a + Number(l.hours || 0), 0);
                return (
                  <tr key={r.id}>
                    <td>
                      <bdi>{r.teacher_name_raw}</bdi>
                      {r.lines.length > 1 ? (
                        <span className="t-meta"> · <span className="num">
                          {fmtNum(r.lines.length)}</span> أسعار</span>
                      ) : null}
                      {r.data_quality.needs_review ? (
                        <NeedsReviewMark q={r.data_quality} compact />
                      ) : null}
                    </td>
                    <td className="t-desc">{monthLabel(r.month)}</td>
                    <td><span className="num">{fmtNum(Math.round(hours * 100) / 100)}</span></td>
                    <td><Money v={r.total_net_usd} /></td>
                    <td>{r.exchange_rate ? <span className="num">{r.exchange_rate}</span> : <Dash />}</td>
                    <td><Money v={r.total_egp} cur="ج.م " /></td>
                    <td><Money v={r.sent_egp} cur="ج.م " /></td>
                    <td className="t-desc">
                      {r.destinations.length === 0 ? <span className="t-meta">— مفيش وجهة</span>
                        : r.destinations.map((d, i) => (
                            <div key={i}>
                              {d.method_ar}
                              {d.bank_name_ar ? ` · ${d.bank_name_ar}` : ""}
                              {d.account_raw ? <> · <bdi className="num">{d.account_raw}</bdi></>
                                : d.account_present ? <span className="t-meta"> · الرقم محجوب</span>
                                : null}
                            </div>
                          ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {meta && meta.totalPages > 1 ? (
        <Pager page={meta.page} totalPages={meta.totalPages} total={meta.total} onPage={setPage} />
      ) : null}
    </div>
  );
}

function StaffTab({ query, month, onClearSearch }:
                  { query: string; month: string; onClearSearch: () => void }) {
  const { state, load, setPage } = usePayouts<StaffPayoutRow>(listStaffPayouts, query, month);
  const rows = state.kind === "ready" ? state.rows : [];
  const meta = state.kind === "ready" ? state.meta : null;
  return (
    <div className="reg-card" style={{ marginTop: 12 }}>
      <div className="reg-head">
        <span className="sec-title">
          {meta ? (<><span className="num">{fmtNum(meta.total)}</span> راتب · إجمالي{" "}
                     <Money v={meta.sum} cur="ج.م " /></>) : "رواتب الموظفين"}
        </span>
      </div>
      <PayoutStates state={state} query={query} onRetry={() => void load()}
                    onClear={onClearSearch} emptyTitle="لسه مفيش رواتب موظفين" />
      {rows.length > 0 ? (
        <div className="reg-scroll">
          <table className="tbl">
            <thead>
              <tr>
                <th>الموظف</th><th>الشهر</th><th>الأساسي</th><th>مكافآت</th>
                <th>مصاريف سحب</th><th>خصومات</th><th>الإجمالي</th><th>الصرف</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <bdi>{r.staff_name_raw}</bdi>
                    {r.data_quality.needs_review ? (
                      <NeedsReviewMark q={r.data_quality} compact />
                    ) : null}
                  </td>
                  <td className="t-desc">{monthLabel(r.month)}</td>
                  <td><Money v={r.base_egp} cur="ج.م " /></td>
                  <td><Money v={r.bonus_egp} cur="ج.م " /></td>
                  {/* ⚠️ «مصاريف سحب» بتتزاد مش بتتخصم — الأكاديمية بتتحمّلها */}
                  <td><Money v={r.withdrawal_fee_egp} cur="ج.م " /></td>
                  <td><Money v={r.deduction_egp} cur="ج.م " /></td>
                  <td><strong><Money v={r.total_egp} cur="ج.م " /></strong></td>
                  <td className="t-desc">
                    {r.destination ? (
                      <>
                        {r.destination.method_ar}
                        {r.destination.bank_name_ar ? ` · ${r.destination.bank_name_ar}` : ""}
                        {r.destination.account_raw
                          ? <> · <bdi className="num">{r.destination.account_raw}</bdi></>
                          : r.destination.account_present
                            ? <span className="t-meta"> · الرقم محجوب</span> : null}
                      </>
                    ) : <span className="t-meta">— مفيش وجهة</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {meta && meta.totalPages > 1 ? (
        <Pager page={meta.page} totalPages={meta.totalPages} total={meta.total} onPage={setPage} />
      ) : null}
    </div>
  );
}

function PayoutStates<T>({ state, query, onRetry, onClear, emptyTitle }: {
  state: PayoutState<T>; query: string; onRetry: () => void;
  onClear: () => void; emptyTitle: string;
}) {
  if (state.kind === "loading") return <LoadingState rows={5} />;
  if (state.kind === "offline") return <OfflineState onRetry={onRetry} />;
  if (state.kind === "error") return <ErrorState messageAr={state.messageAr} onRetry={onRetry} />;
  if (state.rows.length > 0) return null;
  if (query) return <NoResultsState query={query} onClear={onClear} />;
  return <EmptyState title={emptyTitle} hint="الدفاتر بتترفع من مسار الاستيراد" />;
}

/* ══════════════════════════ الترقيم ══════════════════════════ */

/**
 * ⚠️ العدّ من `meta` بتاعة السيستم مش من الحالة المحلية — عشان «عرض ١–١٥
 *    من ٤٠٦» تقول الحقيقة حتى لو السيرفر صحّح الصفحة.
 */
function Pager({ page, totalPages, total, onPage }: {
  page: number; totalPages: number; total: number; onPage: (p: number) => void;
}) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "10px 14px" }}>
      <button type="button" className="filt" disabled={page <= 1}
              onClick={() => onPage(page - 1)}>السابق</button>
      <span className="t-meta">
        صفحة <span className="num">{fmtNum(page)}</span> من{" "}
        <span className="num">{fmtNum(totalPages)}</span>
        {" · "}<span className="num">{fmtNum(total)}</span> سجل
      </span>
      <button type="button" className="filt" disabled={page >= totalPages}
              onClick={() => onPage(page + 1)}>التالي</button>
    </div>
  );
}

/* ══════════════════════════ رفع دفاتر الشهر ══════════════════════════ */

/**
 * 🔴 **الملفات ما بتطلعش من جهاز الموظف كملفات إكسل.** المتصفح بيقراها
 *    ويبعت الخانات، لأن تحليل xlsx على السيرفر سطح هجوم (zip متفجّر ·
 *    كيانات XML خارجية · استنزاف ذاكرة) و`openpyxl` مش في صورة الإنتاج.
 *
 * ⛔ **والمتصفح ما بيفسّرش حاجة** — بيبعت القيمة الخام وصيغة عرضها،
 *    والسيرفر هو اللي بيقرر دي مدة ولا تاريخ ولا رقم. يعني فيه **تطبيق
 *    واحد** لكل فخ. اتحقّق على الداتا الحقيقية: ٧٣٩ مستند من المسارين
 *    طلعوا مطابقين بالحرف.
 *
 * 🔒 **معاينة إجبارية قبل الكتابة.** الكتابة بتبعت بصمة المعاينة، فلو
 *    الملف اتغيّر بينهم الكتابة بتترفض. ومحدش بيكتب دفترًا ما شافش أرقامه.
 */
const BOOKS = [
  { key: "payments" as const, label: "المتحصلات", hint: "دفتر «متحصلات» السنوي" },
  { key: "teacher_payouts" as const, label: "رواتب المعلمين", hint: "دفتر «رواتب معلمين»" },
  { key: "staff_payouts" as const, label: "رواتب الموظفين", hint: "دفتر «رواتب موظفين»" },
];

type Picked = Partial<Record<(typeof BOOKS)[number]["key"], { name: string; sheets: XlsxSheet[] }>>;

function UploadTab({ onImported, onGoPayments }:
                   { onImported: () => void; onGoPayments: () => void }) {
  const [picked, setPicked] = useState<Picked>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [prev, setPrev] = useState<ImportPreview | null>(null);
  const [done, setDone] = useState<ImportPreview | null>(null);

  async function pick(key: keyof Picked, file: File | undefined) {
    if (!file) return;
    setErr(null); setPrev(null); setDone(null);
    setBusy(`بقرا «${file.name}»…`);
    try {
      const sheets = await readXlsx(file);
      setPicked((p) => ({ ...p, [key]: { name: file.name, sheets } }));
    } catch (e) {
      // ⛔ ممنوع الفشل الصامت — الموظف لازم يعرف الملف ده مالوش لازمة ليه
      console.error("[accounts] قراءة الدفتر فشلت", e);
      setErr(e instanceof XlsxError ? e.messageAr : errorMessageAr(e));
    } finally {
      setBusy(null);
    }
  }

  async function runPreview() {
    setErr(null); setDone(null);
    setBusy("بحسب الأرقام…");
    try {
      const books = Object.fromEntries(
        Object.entries(picked).map(([k, v]) => [k, v!.sheets]),
      );
      setPrev(await uploadFinanceBooks(buildFinancePayload(books as never)));
    } catch (e) {
      console.error("[accounts] المعاينة فشلت", e);
      setErr(errorMessageAr(e));
    } finally { setBusy(null); }
  }

  async function commit() {
    if (!prev) return;
    setErr(null);
    setBusy("بكتب في السيستم…");
    try {
      const books = Object.fromEntries(
        Object.entries(picked).map(([k, v]) => [k, v!.sheets]),
      );
      const res = await uploadFinanceBooks(buildFinancePayload(books as never),
                                           { apply: true, confirmHash: prev.fileHash });
      setDone(res); setPrev(null); setPicked({});
      onImported();
    } catch (e) {
      console.error("[accounts] الكتابة فشلت", e);
      setErr(errorMessageAr(e));
    } finally { setBusy(null); }
  }

  const anyPicked = Object.keys(picked).length > 0;

  return (
    <div className="reg-card" style={{ marginTop: 12, padding: 16 }}>
      <div className="sec-title">رفع دفاتر الشهر</div>
      <div className="t-meta" style={{ lineHeight: 1.9, marginBottom: 12 }}>
        اختار الدفاتر اللي اتحدّثت (مش لازم تلاتتهم)، وهتشوف الأرقام
        <strong> قبل </strong>ما تتكتب. الرفع بيحدّث اللي موجود وما بيكرّرش،
        فتقدر ترفع نفس الدفتر تاني بعد أي تعديل.
      </div>

      <div style={{ display: "grid", gap: 10 }}>
        {BOOKS.map((b) => (
          <label key={b.key}
                 style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ minWidth: 130, fontWeight: 600 }}>{b.label}</span>
            <input type="file" accept=".xlsx" className="inp"
                   onChange={(e) => void pick(b.key, e.target.files?.[0])} />
            {picked[b.key] ? (
              <span className="t-meta">
                ✅ <bdi>{picked[b.key]!.name}</bdi> ·{" "}
                <span className="num">{fmtNum(picked[b.key]!.sheets.length)}</span> تبويبة
              </span>
            ) : <span className="t-meta">{b.hint}</span>}
          </label>
        ))}
      </div>

      {busy ? <div className="t-meta" style={{ marginTop: 12 }} role="status">{busy}</div> : null}
      {err ? (
        <div className="reg-card" style={{ padding: 10, marginTop: 12 }} role="alert">
          ⚠️ {err}
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <button type="button" className="filt on" disabled={!anyPicked || busy !== null}
                onClick={() => void runPreview()}>
          اعرض الأرقام قبل ما تتكتب
        </button>
        {anyPicked ? (
          <button type="button" className="filt" disabled={busy !== null}
                  onClick={() => { setPicked({}); setPrev(null); setDone(null); }}>
            ابدأ من الأول
          </button>
        ) : null}
      </div>

      {prev ? (
        <PreviewReport report={prev} onCommit={() => void commit()} busy={busy !== null} />
      ) : null}
      {done ? (
        <>
          <PreviewReport report={done} />
          <div style={{ marginTop: 12 }}>
            <button type="button" className="filt on" onClick={onGoPayments}>
              روح للمتحصلات واربطها بأسرها
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

/**
 * 🔑 **التقرير ده مش تفصيلة عرض — هو الحارس.**
 *    الفرق بين حسابنا وحساب الشيت لكل شهر هو اللي كشف مبلغًا مكتوبًا كنص
 *    إكسل ما بيجمعهوش، ومجموع شهر بيبدأ من الصف الغلط فبيسقّط راتب معلم.
 * ⛔ والسيستم **بيبلّغ وما بيصلّحش** — التصحيح من عندنا بيخفي غلطة محاسبية.
 */
function PreviewReport({ report, onCommit, busy }: {
  report: ImportPreview; onCommit?: () => void; busy?: boolean;
}) {
  const applied = report.mode === "applied";
  return (
    <div style={{ marginTop: 16 }}>
      <div className="sec-title">
        {applied ? "✅ اتكتب في السيستم" : "معاينة — لسه مفيش أي كتابة"}
      </div>

      {report.books.map((b) => (
        <div key={b.key} className="reg-card" style={{ marginTop: 10 }}>
          <div className="reg-head">
            <span className="sec-title">
              {b.labelAr} · <span className="num">{fmtNum(b.records)}</span> سجل
            </span>
          </div>
          <div className="reg-scroll">
            <table className="tbl">
              <thead>
                <tr><th>الشهر</th><th>حسابنا</th><th>مجموع الشيت</th><th>الفرق</th></tr>
              </thead>
              <tbody>
                {b.months.map((m) => {
                  const diff = m.diff === null ? null : Number(m.diff);
                  const clean = diff !== null && Math.abs(diff) < 0.05;
                  return (
                    <tr key={m.month}>
                      <td className="t-desc">{monthLabel(m.month)}</td>
                      <td><Money v={m.computed} cur="" /></td>
                      <td>{m.declared === null ? <Dash /> : <Money v={m.declared} cur="" />}</td>
                      <td>
                        {m.diff === null ? <span className="t-meta">مفيش مجموع في الشيت</span>
                          : clean ? "✅"
                          : <strong><Money v={m.diff} cur="" /></strong>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {b.warnings.length > 0 ? (
            <div style={{ padding: "10px 14px", lineHeight: 1.9 }}>
              {b.warnings.map((w, i) => (
                <div key={i} className="t-meta">⚠️ <bdi>{w}</bdi></div>
              ))}
              <div className="t-meta" style={{ marginTop: 6 }}>
                السيستم <strong>بيبلّغ وما بيصلّحش</strong> — الأرقام دي زي ما هي
                في الشيت، وتصحيحها قرار المحاسبة.
              </div>
            </div>
          ) : null}
          {Object.keys(b.tagged).length > 0 ? (
            <div className="t-meta" style={{ padding: "0 14px 10px" }}>
              محتاج مراجعة:{" "}
              {Object.entries(b.tagged).map(([k, n]) => `${k} (${fmtNum(n)})`).join(" · ")}
            </div>
          ) : null}
          {b.skipped.map((sk, i) => (
            <div key={i} className="t-meta" style={{ padding: "0 14px 10px" }}>⏭️ {sk}</div>
          ))}
        </div>
      ))}

      {applied && report.written ? (
        <div className="t-meta" style={{ marginTop: 10, lineHeight: 1.9 }}>
          اتكتب:{" "}
          {Object.entries(report.written)
            .map(([k, n]) => `${k}: ${fmtNum(n)}`).join(" · ")}
          {report.linked?.families ? (
            <>
              {" · "}اترَبط بأسر تلقائيًا:{" "}
              <span className="num">{fmtNum(report.linked.families.matched ?? 0)}</span>
            </>
          ) : null}
        </div>
      ) : null}

      {!applied && onCommit ? (
        <div style={{ marginTop: 12 }}>
          <button type="button" className="filt on" disabled={busy} onClick={onCommit}>
            راجعت الأرقام — اكتبها في السيستم
          </button>
          <span className="t-meta" style={{ marginInlineStart: 10 }}>
            الكتابة بتحدّث الموجود وما بتكرّرش.
          </span>
        </div>
      ) : null}
    </div>
  );
}
